import { parsePhoneNumberWithError } from "libphonenumber-js"
import { v4 as uuid } from "uuid"

type CapitalizeMode = "first-word" | "all-words"
type UncapitalizeMode = "first-word" | "all-words"

class JuloUtilsClass {
  private static instance: JuloUtilsClass

  private constructor() {}

  public static getInstance(): JuloUtilsClass {
    if (!JuloUtilsClass.instance) {
      JuloUtilsClass.instance = new JuloUtilsClass()
    }

    return JuloUtilsClass.instance
  }

  capitalizeName(firstName: string, lastName?: string | null): string {
    let name = firstName
    if (lastName) name += ` ${lastName}`
    return this.capitalize(name, "all-words")
  }

  /**
   * For greeting a customer by first name: trims stray whitespace and
   * uppercases the first letter, leaving the rest as typed ("anna-Karin "
   * → "Anna-Karin").
   */
  formatFirstName(firstName: string): string {
    const trimmed = firstName.trim()
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
  }

  capitalize(str: string, mode: CapitalizeMode = "first-word") {
    if (mode === "first-word") return this.capitalizeWord(str)

    const result: string[] = []

    for (const word of str.split(" ")) {
      result.push(this.capitalizeWord(word))
    }

    return result.join(" ")
  }

  private capitalizeWord(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  }

  uncapitalize(str: string, mode: UncapitalizeMode = "first-word") {
    if (mode === "first-word") return this.uncapitalizeWord(str)

    const result: string[] = []

    for (const word of str.split(" ")) {
      result.push(this.uncapitalizeWord(word))
    }

    return result.join(" ")
  }

  private uncapitalizeWord(word: string): string {
    return word.toLowerCase()
  }

  uuid(): string {
    return uuid()
  }

  formatSimplePrice(
    price: number,
    locale: Intl.LocalesArgument = "sv-SE",
    options: Intl.NumberFormatOptions = {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
    },
  ) {
    return Intl.NumberFormat(locale, options).format(price)
  }

  calculateCoordinateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number {
    const R = 6371 // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1)
    const dLng = this.toRadians(lng2 - lng1)

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180)
  }

  parsePhoneNumber(phoneNumber: string) {
    return parsePhoneNumberWithError(phoneNumber).formatInternational()
  }

  getBackLink(currentUrl: string, jumpStep = 1): string {
    const linkAsList = currentUrl.split("/")
    for (let i = 0; i < jumpStep; i++) {
      linkAsList.pop()
    }
    return linkAsList.join("/")
  }

  getAllCombinations<T>(arr: T[]): T[][] {
    const result: T[][] = [[]]

    const generate = (combo: T[], index: number) => {
      for (let i = index; i < arr.length; i++) {
        const item = arr[i]
        if (item !== undefined) {
          const newCombo = [...combo, item]
          result.push(newCombo)
          generate(newCombo, i + 1)
        }
      }
    }

    generate([], 0)
    return result
  }

  indexBy<T extends object, K extends keyof T>(
    list: T[],
    key: K,
  ): Record<Extract<T[K], PropertyKey>, T> {
    return Object.fromEntries(
      list.map((item) => [item[key] as PropertyKey, item]),
    ) as Record<Extract<T[K], PropertyKey>, T>
  }

  closestHundredUp(value: number): number {
    return Math.ceil(value / 100) * 100
  }

  roundPrice(value: number): number {
    return Math.round(value * 100) / 100
  }
}

export const JuloUtils = JuloUtilsClass.getInstance()
